import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardBody, Button } from './BaseComponents';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * ErrorBoundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree and displays
 * a fallback UI instead of crashing the entire application. Enhanced to handle
 * new API error formats and provide better user experience.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log specific API-related errors
    if (this.isApiError(error)) {
      console.error('API Error caught by ErrorBoundary:', {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack
      });
    }

    // Log network-related errors
    if (this.isNetworkError(error)) {
      console.error('Network Error caught by ErrorBoundary:', {
        message: error.message,
        isOnline: navigator.onLine,
        userAgent: navigator.userAgent
      });
    }
  }

  private isApiError(error: Error): boolean {
    return error.message.includes('API Error') || 
           error.message.includes('410') ||
           error.message.includes('deprecated') ||
           error.message.includes('Failed to fetch');
  }

  private isNetworkError(error: Error): boolean {
    return error.message.includes('Network Error') ||
           error.message.includes('fetch') ||
           error.message.includes('NETWORK_ERROR') ||
           !navigator.onLine;
  }

  private getErrorType(): 'api' | 'network' | 'generic' {
    const { error } = this.state;
    if (!error) return 'generic';
    
    if (this.isApiError(error)) return 'api';
    if (this.isNetworkError(error)) return 'network';
    return 'generic';
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const errorType = this.getErrorType();
      const { error } = this.state;

      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="max-w-lg w-full">
            <CardBody>
              <div className="text-center">
                {/* Error Icon */}
                <div className="mx-auto mb-4 icon-circle-lg">
                  {errorType === 'network' ? (
                    <div className="bg-orange-100 icon-circle-lg">
                      <svg className="w-8 h-8 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  ) : errorType === 'api' ? (
                    <div className="bg-blue-100 icon-circle-lg">
                      <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                  ) : (
                    <div className="bg-red-100 icon-circle-lg">
                      <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Error Title and Message */}
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {errorType === 'network' && 'Connection Problem'}
                  {errorType === 'api' && 'Service Unavailable'}
                  {errorType === 'generic' && 'Something went wrong'}
                </h2>

                <p className="text-gray-600 mb-6">
                  {errorType === 'network' && (
                    'Please check your internet connection and try again.'
                  )}
                  {errorType === 'api' && (
                    'The service is temporarily unavailable. Please try again in a moment.'
                  )}
                  {errorType === 'generic' && (
                    'An unexpected error occurred. Refreshing the page might help.'
                  )}
                </p>

                {/* Error Details (Development only) */}
                {process.env.NODE_ENV === 'development' && error && (
                  <div className="bg-gray-50 rounded-md p-4 mb-6 text-left">
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Error Details:</h3>
                    <div className="text-xs text-gray-600 font-mono break-all">
                      <div className="mb-2">
                        <strong>Message:</strong> {error.message}
                      </div>
                      {error.stack && (
                        <div>
                          <strong>Stack:</strong>
                          <pre className="mt-1 whitespace-pre-wrap text-xs">
                            {error.stack.split('\n').slice(0, 5).join('\n')}
                            {error.stack.split('\n').length > 5 && '\n...'}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    variant="primary"
                    onClick={this.handleRetry}
                    className="min-w-24"
                  >
                    Try Again
                  </Button>
                  
                  <Button
                    variant="secondary"
                    onClick={this.handleReload}
                    className="min-w-24"
                  >
                    Reload Page
                  </Button>

                  {errorType === 'network' && (
                    <Button
                      variant="secondary"
                      onClick={() => window.location.href = '/'}
                      className="min-w-24"
                    >
                      Go Home
                    </Button>
                  )}
                </div>

                {/* Network Status */}
                {errorType === 'network' && (
                  <div className="mt-4 text-sm text-gray-500">
                    Network Status: {navigator.onLine ? 'Online' : 'Offline'}
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;